import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRivalry } from '../contexts/RivalryContext';

/**
 * Flow for creating a new rivalry
 */
export function CreateRivalryFlow() {
  const navigate = useNavigate();
  const { createRivalry, loading } = useRivalry();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    maxParticipants: 5,
    rivalryType: 'standard',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Rivalry name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (formData.maxParticipants < 2 || formData.maxParticipants > 10) {
      newErrors.maxParticipants = 'Must be between 2 and 10 participants';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const result = await createRivalry({
      name: formData.name,
      description: formData.description,
      maxParticipants: formData.maxParticipants,
      rivalryType: formData.rivalryType,
    });

    if (result.success) {
      navigate(`/rivalries/${result.rivalry.id}`);
    } else {
      setErrors({ submit: result.error.message || 'Failed to create rivalry' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button onClick={() => navigate('/rivalries')} className="text-gray-400 hover:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Rivalries
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">Create New Rivalry</h1>
          <p className="text-gray-400">Set up a new competition with your friends</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-8">
          {/* Rivalry Name */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-white font-semibold mb-2">
              Rivalry Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Morning Warriors, Gym Legends"
              className={`w-full bg-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 ${
                errors.name ? 'ring-2 ring-red-500' : 'focus:ring-blue-500'
              }`}
              maxLength={100}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label htmlFor="description" className="block text-white font-semibold mb-2">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="What's this rivalry about?"
              rows={4}
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={500}
            />
            <p className="text-gray-400 text-sm mt-1">{formData.description.length}/500 characters</p>
          </div>

          {/* Max Participants */}
          <div className="mb-6">
            <label htmlFor="maxParticipants" className="block text-white font-semibold mb-2">
              Maximum Participants
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                id="maxParticipants"
                min="2"
                max="10"
                value={formData.maxParticipants}
                onChange={(e) => handleChange('maxParticipants', parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-white font-bold text-2xl w-12 text-center">{formData.maxParticipants}</span>
            </div>
            <p className="text-gray-400 text-sm mt-2">You'll be the first participant. Invite others to join!</p>
            {errors.maxParticipants && <p className="text-red-500 text-sm mt-1">{errors.maxParticipants}</p>}
          </div>

          {/* Rivalry Type */}
          <div className="mb-8">
            <label className="block text-white font-semibold mb-3">Rivalry Type</label>
            <div className="grid grid-cols-1 gap-3">
              <label
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.rivalryType === 'standard'
                    ? 'border-blue-500 bg-blue-900 bg-opacity-20'
                    : 'border-gray-700 bg-gray-700'
                }`}
              >
                <input
                  type="radio"
                  name="rivalryType"
                  value="standard"
                  checked={formData.rivalryType === 'standard'}
                  onChange={(e) => handleChange('rivalryType', e.target.value)}
                  className="mr-3"
                />
                <div>
                  <p className="text-white font-semibold">Standard</p>
                  <p className="text-gray-400 text-sm">Individual competition, everyone for themselves</p>
                </div>
              </label>

              <label
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.rivalryType === 'team'
                    ? 'border-blue-500 bg-blue-900 bg-opacity-20'
                    : 'border-gray-700 bg-gray-700'
                }`}
              >
                <input
                  type="radio"
                  name="rivalryType"
                  value="team"
                  checked={formData.rivalryType === 'team'}
                  onChange={(e) => handleChange('rivalryType', e.target.value)}
                  className="mr-3"
                />
                <div>
                  <p className="text-white font-semibold">Team (Coming Soon)</p>
                  <p className="text-gray-400 text-sm">Split into teams and compete together</p>
                </div>
              </label>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-6 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-4">
              <p className="text-red-500">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/rivalries')}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Rivalry'}
            </button>
          </div>
        </form>

        {/* Info Card */}
        <div className="mt-8 bg-blue-900 bg-opacity-20 border border-blue-500 rounded-lg p-6">
          <h3 className="text-white font-bold mb-2 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            How Rivalries Work
          </h3>
          <ul className="text-gray-300 text-sm space-y-2">
            <li>• Each rivalry runs on monthly seasons</li>
            <li>• Complete workouts to earn points and climb the leaderboard</li>
            <li>• Top 3 finishers each month win prizes and bragging rights</li>
            <li>• Chat with rivals and track everyone's progress in real-time</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
